import { AfterViewInit, ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatPaginator, MatPaginatorIntl } from '@angular/material/paginator';
import { BillableItem, BillingService, Frequency, ListSubscriptionsFilter, frequencyToApi } from '@galaxy/billing';
import { TranslateService } from '@ngx-translate/core';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { BehaviorSubject, Observable, Subscription, combineLatest } from 'rxjs';
import {
  debounceTime,
  delay,
  finalize,
  map,
  shareReplay,
  skip,
  startWith,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { ListSubscriptionsDataSource } from './subscription-list.data-source';

import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { Frequency as FrequencyApi } from '@vendasta/billing';
import {
  CheckboxFilterControl,
  DateFilterControl,
  FilterControl,
  FilterGroup,
  SearchFilterControl,
} from '@vendasta/va-filter2';
import { MatPaginatorNoTotalDisplayed } from '@vendasta/va-filter2-table';
import { ChangeFrequencyDialogComponent } from './change-frequency-dialog/change-frequency.component';
import { ChangePriceDialogComponent } from './change-price-dialog/change-price-dialog.component';
import { ChangeRenewalDialogComponent } from './change-renewal-dialog copy/change-renewal.component';

interface SubscriptionColumn {
  id: string;
  name: string;
}

const ALL_COLUMNS: SubscriptionColumn[] = [
  {
    id: 'customerName',
    name: 'Customer',
  },
  {
    id: 'productName',
    name: 'Product',
  },
  {
    id: 'start',
    name: 'Start Date',
  },
  {
    id: 'frequency',
    name: 'Frequency',
  },
  {
    id: 'nextRenewal',
    name: 'Next Renewal Date',
  },
  {
    id: 'end',
    name: 'End Date',
  },
  {
    id: 'customUnitPrice',
    name: 'Custom Unit Price',
  },
  {
    id: 'actions',
    name: 'Actions',
  },
];

@Component({
  selector: 'billing-ui-subscription-list',
  templateUrl: './subscription-list.component.html',
  styleUrls: ['./subscription-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: MatPaginatorIntl, useClass: MatPaginatorNoTotalDisplayed }],
})
export class SubscriptionListComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('subscriptionPaginator', { static: true }) paginator: MatPaginator;
  @Input() columnIds: string[]; // Columns to display, default: all.
  @Input() filters: Observable<ListSubscriptionsFilter>;
  @Input() businessSelectable = false;
  @Input() businessTooltip = '';
  @Input() productSelectable = false;
  @Input() productTooltip = '';
  @Input() isAdmin = false;
  @Input() businessSelectedURL: (businessID: string) => string;
  @Input() skuSelectedURL: (sku: string) => string;

  public length$: Observable<number>;
  public displayedColumns: string[];
  public displayedColumnsNames: string[];
  public dataSource: ListSubscriptionsDataSource;
  public filterGroup: FilterGroup;
  private subscriptions: Subscription[] = [];
  private combinedFilters$: Observable<ListSubscriptionsFilter>;
  public exportLoading$$ = new BehaviorSubject(false);

  constructor(
    private subscriptionService: BillingService,
    public dialog: MatDialog,
    private readonly translateService: TranslateService,
    private snackbarService: SnackbarService,
  ) {
    this.filterGroup = new FilterGroup('subscription-list')
      .addToolbarSection(
        new SearchFilterControl('search', this.translateService.instant('SUBSCRIPTIONS.FILTERS.SEARCH')),
      )
      .addSection('End Date', [
        new DateFilterControl('endDateBefore', this.translateService.instant('SUBSCRIPTIONS.FILTERS.DATE.ENDS_BEFORE')),
        new DateFilterControl('endDateAfter', this.translateService.instant('SUBSCRIPTIONS.FILTERS.DATE.ENDS_AFTER')),
      ])
      .addSection('Frequency', [
        new CheckboxFilterControl(
          'oneTime',
          this.translateService.instant('SUBSCRIPTIONS.FILTERS.FREQUENCY.ONE_TIME'),
          false,
          {
            appliedValueMapper: (name) => ({
              name,
              label: this.translateService.instant('SUBSCRIPTIONS.FILTERS.FREQUENCY.ONE_TIME'),
            }),
          },
        ),
        new CheckboxFilterControl(
          'monthly',
          this.translateService.instant('SUBSCRIPTIONS.FILTERS.FREQUENCY.MONTHLY'),
          false,
          {
            appliedValueMapper: (name) => ({
              name,
              label: this.translateService.instant('SUBSCRIPTIONS.FILTERS.FREQUENCY.MONTHLY'),
            }),
          },
        ),
        new CheckboxFilterControl(
          'yearly',
          this.translateService.instant('SUBSCRIPTIONS.FILTERS.FREQUENCY.YEARLY'),
          false,
          {
            appliedValueMapper: (name) => ({
              name,
              label: this.translateService.instant('SUBSCRIPTIONS.FILTERS.FREQUENCY.YEARLY'),
            }),
          },
        ),
      ]);

    this.dataSource = new ListSubscriptionsDataSource(this.subscriptionService);
    this.length$ = this.dataSource.storedSubscriptions$.pipe(
      skip(1),
      withLatestFrom(this.dataSource.hasMore$),
      map(([items, hasMore]) => (hasMore ? items.length + this.paginator.pageSize : items.length)),
    );
  }

  get searchFilterControl(): SearchFilterControl {
    return this.filterGroup.get('search') as SearchFilterControl;
  }

  ngOnInit(): void {
    const columnsToDisplay = this.getColumnsToDisplay(this.columnIds);
    this.displayedColumns = columnsToDisplay.map((c: SubscriptionColumn) => c.id);
    this.displayedColumnsNames = columnsToDisplay.map((c: SubscriptionColumn) => c.name);

    const filterGroup$ = this.filterGroup.valueChanges.pipe(startWith(this.filterGroup.value), debounceTime(500));
    this.combinedFilters$ = combineLatest([filterGroup$, this.filters]).pipe(
      map(([filterGroup, inputFilters]) => {
        return {
          ...inputFilters,
          searchTerm: filterGroup.search || '',
          expiredDateGte: filterGroup.endDateAfter,
          expiredDateLte: filterGroup.endDateBefore,
          frequencies: this.buildFrequencyFilter(filterGroup.oneTime, filterGroup.monthly, filterGroup.yearly),
        };
      }),
      shareReplay(1),
    );

    this.subscriptions.push(
      this.paginator.page.pipe(withLatestFrom(this.combinedFilters$)).subscribe(([, combinedFilters]) => {
        this.dataSource.updatePage(combinedFilters, this.paginator.pageSize, this.paginator.pageIndex);
      }),
    );
  }

  private getColumnsToDisplay(columnIds: string[]): SubscriptionColumn[] {
    if (!(columnIds?.length > 0)) {
      return ALL_COLUMNS;
    }
    return ALL_COLUMNS.filter((column) => columnIds.includes(column.id));
  }

  private buildFrequencyFilter(filterOneTime, filterMonthly, filterYearly: FilterControl): FrequencyApi[] {
    const frequencies: FrequencyApi[] = [];
    if (filterOneTime) {
      frequencies.push(frequencyToApi(Frequency.OneTime));
    }
    if (filterMonthly) {
      frequencies.push(frequencyToApi(Frequency.Monthly));
    }
    if (filterYearly) {
      frequencies.push(frequencyToApi(Frequency.Yearly));
    }

    return frequencies;
  }

  ngAfterViewInit(): void {
    this.subscriptions.push(
      this.combinedFilters$.subscribe((combinedFilters) => {
        this.paginator.firstPage();
        this.dataSource.reload(combinedFilters, this.paginator.pageSize, this.paginator.pageIndex);
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  customerSubtitle(billableItem: BillableItem): string {
    if (this.isAdmin && !!billableItem.customerName) {
      return billableItem.customerId;
    }
    if (billableItem.customerAddress?.address && billableItem.customerAddress?.city) {
      return `${billableItem.customerAddress.address}, ${billableItem.customerAddress.city}`;
    }
    return '';
  }

  public changePrice(b: BillableItem): void {
    this.dialog
      .open(ChangePriceDialogComponent, {
        width: '650px',
        data: {
          productName: b.productName,
          customerName: b.customerName,
          merchantId: b.merchantId,
          customerId: b.customerId,
          sku: b.sku,
          orderId: b.orderId,
          currentUnitPrice: b.customUnitPrice,
        },
      } as MatDialogConfig)
      .afterClosed()
      .pipe(delay(1000), withLatestFrom(this.combinedFilters$))
      .subscribe(([isSuccess, combinedFilters]) => {
        if (isSuccess) {
          this.dataSource.reload(combinedFilters, this.paginator.pageSize, this.paginator.pageIndex);
        }
      });
    return;
  }

  public changeFrequency(b: BillableItem): void {
    this.dialog
      .open(ChangeFrequencyDialogComponent, {
        width: '650px',
        data: {
          productName: b.productName,
          customerName: b.customerName,
          merchantId: b.merchantId,
          customerId: b.customerId,
          sku: b.sku,
          orderId: b.orderId,
          currentFrequency: b.frequency,
        },
      } as MatDialogConfig)
      .afterClosed()
      .pipe(delay(1000), withLatestFrom(this.combinedFilters$))
      .subscribe(([isSuccess, combinedFilters]) => {
        if (isSuccess) {
          this.dataSource.reload(combinedFilters, this.paginator.pageSize, this.paginator.pageIndex);
        }
      });
    return;
  }

  public changeRenewal(b: BillableItem): void {
    this.dialog
      .open(ChangeRenewalDialogComponent, {
        width: '650px',
        data: {
          productName: b.productName,
          customerName: b.customerName,
          merchantId: b.merchantId,
          customerId: b.customerId,
          sku: b.sku,
          orderId: b.orderId,
          nextRenewal: b.nextRenewal,
        },
      } as MatDialogConfig)
      .afterClosed()
      .pipe(delay(1000), withLatestFrom(this.combinedFilters$))
      .subscribe(([isSuccess, combinedFilters]) => {
        if (isSuccess) {
          this.dataSource.reload(combinedFilters, this.paginator.pageSize, this.paginator.pageIndex);
        }
      });
    return;
  }

  public hasRenewalDate(b: BillableItem): boolean {
    return !!b.nextRenewal;
  }

  public hasCustomUnitPrice(b: BillableItem): boolean {
    // TODO: JRedl added this to allow our ads team to get there work done
    // We need to either pass thru the "spend" all the time or add the pricing "allows overrides" or something like
    // that to our pricing "models"
    return b.customUnitPrice > 0 || b.sku === 'MP-WM65PB2J5PL6BCNLB5JJN58DDQWXZVPN';
  }

  public businessRoute(businessID: string): string {
    return this.businessSelectedURL(businessID);
  }

  public skuRoute(sku: string): string {
    return this.skuSelectedURL(sku);
  }

  export(): void {
    this.combinedFilters$
      .pipe(
        take(1),
        tap(() => this.exportLoading$$.next(true)),
        switchMap((filters) => this.subscriptionService.exportSubscriptions(filters)),
        finalize(() => this.exportLoading$$.next(false)),
      )
      .subscribe(
        () => this.snackbarService.openSuccessSnack('SUBSCRIPTIONS.EXPORT.SUCCESS'),
        () => this.snackbarService.openErrorSnack('SUBSCRIPTIONS.EXPORT.ERROR'),
      );
  }
}
